import { parseCurrency, roundMoney } from "./utils";
import { TRANSACTION_KINDS, isCreditCardCharge, isCreditCardInvoice } from "./transaction-types";

function clampDay(year, month, day) {
  const lastDay = new Date(year, month + 1, 0).getDate();
  return Math.min(Math.max(parseInt(day, 10) || 1, 1), lastDay);
}

function atNoon(year, month, day) {
  return new Date(year, month, day, 12, 0, 0, 0);
}

function shiftYearMonth(year, month, offset) {
  const date = new Date(year, month + offset, 1, 12, 0, 0, 0);
  return {
    year: date.getFullYear(),
    month: date.getMonth(),
  };
}

export function getInvoiceMonthKey(year, month) {
  return `${year}-${String(month + 1).padStart(2, "0")}`;
}

export function getCardInvoiceKey(card = {}, year, month) {
  return `${card.id}:${getInvoiceMonthKey(year, month)}`;
}

export function parseCardInvoiceKey(invoiceKey = "") {
  const [, monthKey] = String(invoiceKey).split(":");
  const [yearValue, monthValue] = String(monthKey || "").split("-").map((value) => parseInt(value, 10));

  if (!Number.isInteger(yearValue) || !Number.isInteger(monthValue)) {
    return null;
  }

  return {
    year: yearValue,
    month: monthValue - 1,
  };
}

export function getCardInvoiceCycle(card = {}, purchaseDateValue = new Date()) {
  const purchaseDate = new Date(purchaseDateValue);
  const closingDay = parseInt(card.closingDay, 10) || 1;
  const dueDay = parseInt(card.dueDay, 10) || 10;
  let closingYear = purchaseDate.getFullYear();
  let closingMonth = purchaseDate.getMonth();

  if (purchaseDate.getDate() > closingDay) {
    closingMonth += 1;
    if (closingMonth > 11) {
      closingMonth = 0;
      closingYear += 1;
    }
  }

  const closingDate = atNoon(
    closingYear,
    closingMonth,
    clampDay(closingYear, closingMonth, closingDay)
  );

  let dueYear = closingYear;
  let dueMonth = closingMonth;
  if (dueDay <= closingDay) {
    dueMonth += 1;
    if (dueMonth > 11) {
      dueMonth = 0;
      dueYear += 1;
    }
  }

  const dueDate = atNoon(dueYear, dueMonth, clampDay(dueYear, dueMonth, dueDay));

  return {
    closingDate,
    dueDate,
    invoiceYear: dueYear,
    invoiceMonth: dueMonth,
    invoiceKey: getCardInvoiceKey(card, dueYear, dueMonth),
  };
}

export function getCardInvoiceCycleForDueMonth(card = {}, year, month) {
  const closingDay = parseInt(card.closingDay, 10) || 1;
  const dueDay = parseInt(card.dueDay, 10) || 10;
  let closingYear = year;
  let closingMonth = month;

  if (dueDay <= closingDay) {
    closingMonth -= 1;
    if (closingMonth < 0) {
      closingMonth = 11;
      closingYear -= 1;
    }
  }

  const closingDate = atNoon(
    closingYear,
    closingMonth,
    clampDay(closingYear, closingMonth, closingDay)
  );
  const dueDate = atNoon(year, month, clampDay(year, month, dueDay));

  return {
    closingDate,
    dueDate,
    invoiceYear: year,
    invoiceMonth: month,
    invoiceKey: getCardInvoiceKey(card, year, month),
  };
}

export function getCardInvoiceCycleFromKey(card = {}, invoiceKey) {
  const parsed = parseCardInvoiceKey(invoiceKey);
  if (!parsed) return getCardInvoiceCycle(card, new Date());
  return getCardInvoiceCycleForDueMonth(card, parsed.year, parsed.month);
}

export function shiftCardInvoiceKey(card = {}, invoiceKey, offset) {
  const parsed = parseCardInvoiceKey(invoiceKey);
  if (!parsed) return invoiceKey;

  const shifted = shiftYearMonth(parsed.year, parsed.month, offset);
  return getCardInvoiceKey(card, shifted.year, shifted.month);
}

export function getCardInvoiceOptions(card = {}, dateValue = new Date(), before = 2, after = 10) {
  const baseCycle = getCardInvoiceCycle(card, dateValue);
  const options = [];

  for (let offset = -before; offset <= after; offset += 1) {
    const invoiceKey = shiftCardInvoiceKey(card, baseCycle.invoiceKey, offset);
    const cycle = getCardInvoiceCycleFromKey(card, invoiceKey);
    options.push({
      invoiceKey,
      cycle,
      label: cycle.dueDate.toLocaleString("pt-BR", {
        month: "long",
        year: "numeric",
      }),
      description: `Vence em ${cycle.dueDate.toLocaleDateString("pt-BR")}`,
    });
  }

  return options;
}

export function getCreditCardCharges(transactions = []) {
  return transactions.filter(isCreditCardCharge);
}

export function getCreditCardInvoices(transactions = []) {
  return transactions.filter(isCreditCardInvoice);
}

export function getInvoiceCharges(transactions = [], card = {}, invoiceKey) {
  return getCreditCardCharges(transactions).filter((tx) => (
    tx.card_id === card.id && tx.invoice_key === invoiceKey
  ));
}

export function getInvoiceTotal(charges = []) {
  return roundMoney(charges.reduce((sum, tx) => sum + getInvoiceChargeAmount(tx), 0));
}

export function getInvoiceTransaction(transactions = [], card = {}, invoiceKey) {
  return getCreditCardInvoices(transactions).find((tx) => (
    tx.card_id === card.id && tx.invoice_key === invoiceKey
  )) || null;
}

export function getInvoiceChargeAmount(charge = {}) {
  const amount = Math.abs(parseCurrency(charge.amount));
  return charge.amount_sign === "+" ? -amount : amount;
}

export function buildCreditCardChargePayload({ card, account, data }) {
  const cycle = data.invoiceKey
    ? getCardInvoiceCycleFromKey(card, data.invoiceKey)
    : getCardInvoiceCycle(card, data.date);

  return {
    description: data.description.trim(),
    amount: roundMoney(parseCurrency(data.amount)),
    amount_sign: data.amountSign || data.amount_sign || "-",
    category: data.category?.trim() || null,
    category_id: data.categoryId || data.category_id || null,
    account: null,
    date: new Date(data.date).toISOString(),
    status: false,
    confirmed: false,
    transaction_kind: TRANSACTION_KINDS.CREDIT_CARD_CHARGE,
    hidden: true,
    affects_reports: false,
    card_id: card.id,
    card_name: card.name,
    account_id: account?.id || card.accountId || null,
    invoice_key: cycle.invoiceKey,
    invoice_due_date: cycle.dueDate.toISOString(),
    invoice_closing_date: cycle.closingDate.toISOString(),
    manual_invoice_key: Boolean(data.invoiceManual),
    card_charge_mode: data.cardChargeMode || "expense",
    details: Array.isArray(data.details) ? data.details : [],
    notes: data.notes || "",
    ...(data.recurrenceData || {}),
  };
}

export function buildCreditCardInvoicePayload({ card, account, cycle, total, existingInvoice = null }) {
  const monthLabel = cycle.dueDate.toLocaleString("pt-BR", {
    month: "long",
    year: "numeric",
  });
  const isPaid = existingInvoice?.status === true;

  return {
    ...(existingInvoice || {}),
    description: `Fatura ${card.name} - ${monthLabel}`,
    amount: roundMoney(total),
    category: "FATURA CARTAO",
    category_id: existingInvoice?.category_id || null,
    account: account?.name || card.accountName || null,
    date: cycle.dueDate.toISOString(),
    status: isPaid,
    confirmed: isPaid,
    realized_at: isPaid ? (existingInvoice.realized_at || cycle.dueDate.toISOString()) : undefined,
    transaction_kind: TRANSACTION_KINDS.CREDIT_CARD_INVOICE,
    card_id: card.id,
    card_name: card.name,
    account_id: account?.id || card.accountId || null,
    invoice_key: cycle.invoiceKey,
    invoice_due_date: cycle.dueDate.toISOString(),
    invoice_closing_date: cycle.closingDate.toISOString(),
    hidden: false,
    affects_reports: true,
  };
}
